/**
 * SimplifiedSpotCard — the structured-card alternative to PokerTable.
 *
 * Built from scratch (NOT recovered from git history) so it inherits
 * today's contract work by construction:
 *   - Canonical position labels (`UTG1`, `LJ`) come straight from
 *     `spot.position` — `isValidSpot` already gated this upstream.
 *   - `historyToSentences` provides the per-street grouped narrative
 *     used in the multi-line history block.
 *   - `spot.question` is rendered inline with the action context.
 *
 * Layout (matches the user's reference screenshot — see
 * notes/spotcard-simplified-2026-05-25.png if archived):
 *
 *   ┌────────────────────────────────────────────┐
 *   │  [STREET] [POS]            95BB EFF   <topRight> │
 *   │              POT                              │
 *   │             15bb                              │
 *   │       2♦  7♥  2♣  J♠   (board)               │
 *   │           YOUR HAND                           │
 *   │             9♣  2♠                            │
 *   │  PREFLOP    sentence · sentence              │
 *   │  FLOP       sentence · sentence · sentence   │
 *   │  TURN       sentence · sentence              │
 *   │  Action on: Hero                              │
 *   │  CO bets 6 into 9 on the turn. Your play?    │
 *   │  <actions slot — Fold / Call / Raise>        │
 *   └────────────────────────────────────────────┘
 *
 * Slot props (`topRight`, `actions`) let the parent SpotCard own the
 * stateful pieces (flag button + reported badge + the TableActionBar
 * or ReportedFooter) without this view component reaching back into
 * report-flow state.
 */

import type { ReactNode } from "react";

import { formatBb } from "@/shared/format";
import { historyToSentences } from "@/features/chat/lib/handHistoryNL";
import type { SpotPayload } from "@/shared/models";
import { PlayingCard } from "@/features/chat/components/PlayingCard";

interface SimplifiedSpotCardProps {
  spot: SpotPayload;
  /** Top-right corner content — typically the report flag button or
   *  the "Reported" badge once the user has flagged the spot. */
  topRight?: ReactNode;
  /** Action bar — either a constructed `<TableActionBar>` or the
   *  post-report `<ReportedFooter>`. Lives inside the card frame so
   *  the user's eye stays on one rounded surface. */
  actions: ReactNode;
}

export function SimplifiedSpotCard({
  spot,
  topRight,
  actions,
}: SimplifiedSpotCardProps) {
  const narratives = historyToSentences(spot.history, spot.street);
  const effStack = effectiveStackBb(spot);

  return (
    <div className="bg-surface border border-border rounded-2xl p-4 sm:p-5 space-y-4">
      {/* Top bar — street + position pills on the left, effective stack
       *  + slot (flag/reported badge) on the right. */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Pill>{spot.street.toUpperCase()}</Pill>
          <Pill>{spot.position}</Pill>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono tabular-nums text-text-muted uppercase tracking-wider">
            {effStack}BB EFF
          </span>
          {topRight}
        </div>
      </div>

      {/* Pot — uppercase eyebrow + big yellow value. */}
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted/70">
          Pot
        </div>
        <div className="text-3xl font-bold text-amber-300 font-mono tabular-nums mt-0.5">
          {formatBb(spot.pot)}
        </div>
      </div>

      {/* Board — 0–5 cards centered. Suppressed entirely on preflop / empty board. */}
      {spot.board.length > 0 && (
        <div className="flex justify-center gap-1.5">
          {spot.board.map((c, i) => (
            <PlayingCard key={`${c}-${i}`} token={c} size="sm" />
          ))}
        </div>
      )}

      {/* Hero hand — eyebrow + two cards, slightly larger than the board
       *  so the user's hand visually anchors. */}
      <div className="space-y-1">
        <div className="text-center text-[10px] uppercase tracking-[0.2em] text-text-muted/70">
          Your Hand
        </div>
        <div className="flex justify-center gap-1.5">
          {spot.hand.map((c, i) => (
            <PlayingCard key={`${c}-${i}`} token={c} size="md" />
          ))}
        </div>
      </div>

      {/* Multi-line history — one row per non-empty street. Sentences
       *  from `historyToSentences` are bullet-separated for the
       *  compact transcript look. */}
      {narratives.length > 0 && (
        <div className="space-y-1 text-xs">
          {narratives.map(({ street, text }) => (
            <div key={street} className="flex gap-3 items-baseline">
              <span className="uppercase tracking-wider text-text-muted/60 text-[10px] min-w-[58px] shrink-0">
                {street}
              </span>
              <span className="text-text/90">{toBullets(text)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action context: who's on action + the LLM's framing question.
       *  `to_act` is part of the wire shape so we trust it as-is. */}
      <div className="space-y-2 pt-1">
        {spot.to_act && (
          <div className="text-xs text-text-muted">
            Action on:{" "}
            <span className="text-text font-medium">{spot.to_act}</span>
          </div>
        )}
        {spot.question && (
          <div className="text-sm text-text/90 leading-snug">
            {spot.question}
          </div>
        )}
      </div>

      {/* Action bar slot — Fold/Call/Raise (or the post-report footer). */}
      <div>{actions}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pure helpers — exported for unit tests. No React, no DOM.
// ---------------------------------------------------------------------------

/** Effective stack = min(hero, villain), in big blinds. Single number
 *  the wire shape gives us; multi-way isn't represented. */
export function effectiveStackBb(spot: SpotPayload): number {
  const eff = Math.min(spot.stacks.hero, spot.stacks.villain);
  // Trim trailing zeros for clean display ("100" not "100.0").
  return Math.round(eff * 10) / 10;
}

/** Convert the period-joined `StreetNarrative.text` into the
 *  bullet-separated transcript form used in the simplified history
 *  block. "CO opened to 2.5bb. You called 2.5bb." → "CO opened to
 *  2.5bb · You called 2.5bb". Trailing periods stripped. */
export function toBullets(periodJoined: string): string {
  return periodJoined
    .split(". ")
    .map((s) => s.replace(/\.$/, "").trim())
    .filter(Boolean)
    .join(" · ");
}

// ---------------------------------------------------------------------------
// Pill — small rounded-border label used in the top bar.
// ---------------------------------------------------------------------------

function Pill({ children }: { children: ReactNode }) {
  return (
    <span className="px-2 py-0.5 rounded-md border border-border text-[11px] font-semibold tracking-wide text-text-muted uppercase">
      {children}
    </span>
  );
}
